<template>
  <div>
    <div class="toolbar-wrapper">
      <cell-tool-bar
        :img-width-percentage="imgWidthPercentage"
        :show-bbox="showBbox"
        :show-normal="showNormal"
        :show-wrong="showWrong"
        :show-excluded="showExcluded"
        :drawing-mode="drawingMode"
        @img-width-percentage="imgWidthPercentage = $event"
        @show-bbox="showBbox = $event"
        @show-normal="showNormal = $event"
        @show-wrong="showWrong = $event"
        @show-excluded="showExcluded = $event"
        @drawing-mode="drawingMode = $event"
      />
    </div>
    <div class="cell-container">
      <template v-for="type in visibleCellTypes">
        <CellGroupBox
          v-if="groupDict[type] && groupDict[type].length > 0"
          :key="type"
          :cell-type="type"
          :is-z-pressing="isZPressing"
          :show-bbox="showBbox"
          :show-normal="showNormal"
          :show-wrong="showWrong"
          :show-excluded="showExcluded"
          :drawing-mode="drawingMode"
          :img-width="imgWidth"
          :is-read-only="isReadOnly"
          @cell-right-click="handleClick"
          @activate="activate"
        />
      </template>
      <cell-type-menu
        ref="vueSimpleContextMenu"
        :options="cellTypes"
        @option-clicked="optionClicked"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, provide } from 'vue';
import type { CheckboxValue } from 'bootstrap-vue-next';

import CellGroupBox from '@/views/components/CellGroupBox.vue';
import CellTypeMenu from '@/views/components/CellTypeMenu.vue';
import { useCells } from '@/composables/useCells';
import { useRepeatTask } from '@/composables/useRepeatTask';
import CellToolBar from '@/views/components/CellToolBar.vue';
import { getDrawOptions, type CellTypeMenuOption, collectNamesFromTree } from '../utils/constants/cellTypeMenuConfig';
import { useConfigStore, useCellGroupStore } from '@/store';
import { useApi, useConstants } from '@/composables/useAppGlobals';
import { reportReviewInjectionKey } from '@/views/components/reportReviewContext';

const config = useConfigStore();
const cellGroups = useCellGroupStore();

const $api = useApi();
const $const = useConstants();

type MenuOrigin = {
  fromType: string;
  fromTileId: number;
};

type CellTypeMenuExpose = {
  showMenu: (event: MouseEvent, origin: MenuOrigin) => void;
  hideContextMenu: () => void;
};

const cellTypes = ref<CellTypeMenuOption[]>([]);
const isZPressing = ref(false);
const imgWidthPercentage = ref('60');
const showBbox = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showNormal = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showWrong = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showExcluded = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const drawingMode = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(false);
const idleTimerId = ref<IntervalHandle | null>(null);
const idleInterval = ref(10000);
const idleCount = ref(0);

const intervalTaskId = ref<IntervalHandle | null>(null);
const repeatInterval = ref(10000);

const slideInfo = computed(() => config.slideInfo);
const cellGroupID = computed(() => config.cellGroupID);
const isReadOnly = computed(() => config.isReadOnly);
const groupDict = computed(() => cellGroups.groupDict);

const imgWidth = computed(() => $const.DEFAULT_CELL_IMG_SIZE * (Number(imgWidthPercentage.value) / 100));

const staticCellTypes = computed(() => {
  const projectKey = $api.utils.getProject();
  const tree = getDrawOptions({ projectKey, consts: $const });
  return collectNamesFromTree(tree); // ensure the order of static cell types is consistent with the menu
});
const visibleCellTypes = computed(() => {
  const merged = new Set<string>([...staticCellTypes.value, ...Object.keys(groupDict.value || {})]);
  return Array.from(merged);
});

function clearTimer() {
  if (idleTimerId.value != null) {
    window.clearInterval(idleTimerId.value);
    idleTimerId.value = null;
  }
}
function setTimer() {
  if (idleTimerId.value != null) return;
  idleTimerId.value = window.setInterval(handleIdleTimeout, idleInterval.value);
}
function handleIdleTimeout() {
  idleCount.value += 1;
  if (idleCount.value >= 12 && intervalTaskId.value) {
    stopRepeat();
  }
}

function activate() {
  idleCount.value = 0;
  if (!intervalTaskId.value && !isReadOnly.value) {
    startRepeat([triggerSave]);
  }
}

async function getCellGroups() {
  const slideIdRaw = slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID];
  if (typeof slideIdRaw !== 'string' || !slideIdRaw) return;
  if (cellGroupID.value !== slideIdRaw) {
    cellGroups.resetGroups();
    const cellInfo = await $api.getCellGroups(slideIdRaw);
    config.setCellGroupID(slideIdRaw);
    cellGroups.setupCellGroups({ groupDict: cellInfo, allCellTypes: staticCellTypes.value });
  }
}

function handleClick(event: MouseEvent, type: string, tileId: number) {
  if (isReadOnly.value) return;
  vueSimpleContextMenu.value?.showMenu?.(event, { fromType: type, fromTileId: tileId });
  activate();
}

function optionClicked({ item, option }: { item: MenuOrigin; option: CellTypeMenuOption }) {
  if (option.name === $const.BBOX_ACTIONS.CLEAR_BBOX) {
    cellGroups.changeBbox({ fromType: item.fromType, tileId: item.fromTileId, bboxes: [] });
    return;
  }
  cellGroups.changeCellGroup({ fromType: item.fromType, tileId: item.fromTileId, toType: option.name });
}

function onZKeyDown(e: KeyboardEvent) {
  if (e.code === 'KeyZ') {
    isZPressing.value = true;
  }
}
function onZKeyUp(e: KeyboardEvent) {
  if (e.code === 'KeyZ') {
    isZPressing.value = false;
  }
}

const reviewContext = useCells();
provide(reportReviewInjectionKey, reviewContext);
const triggerSave = () => { void reviewContext.save(); };
const { startRepeat, stopRepeat } = useRepeatTask({ intervalTaskId, repeatInterval });

const vueSimpleContextMenu = ref<CellTypeMenuExpose | null>(null);

onMounted(() => {
  const drawOptions: CellTypeMenuOption[] = [
    { name: 'bbox action', type: 'divider' },
    ...Object.values($const.BBOX_ACTIONS).map<CellTypeMenuOption>((action) => ({ name: action })),
    { name: 'to type', type: 'divider' },
  ];
  const projectKey = $api.utils.getProject();
  const tree = getDrawOptions({ isRbc: false, projectKey, consts: $const });
  cellTypes.value = drawOptions.concat(tree);
  getCellGroups();
  if (!isReadOnly.value) {
    repeatInterval.value = 30000;
    startRepeat([triggerSave]);
    setTimer();
  }
  document.body.addEventListener('keydown', onZKeyDown);
  document.body.addEventListener('keyup', onZKeyUp);
});

onBeforeUnmount(() => {
  document.body.removeEventListener('keydown', onZKeyDown);
  document.body.removeEventListener('keyup', onZKeyUp);
  clearTimer();
  stopRepeat();
});
</script>
<style lang="scss" scoped>
.cell-container {
  background-color: white;
  margin-top: 12%;
}

.toolbar-wrapper {
  position: relative;
}
</style>
